"use client";

import type { ChannelGroupListing } from "@mlx/contracts";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronRightIcon,
  EllipsisIcon,
  PencilIcon,
  Trash2Icon,
} from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

/*
 * A channel group's section in the navigation, as markup: the marketing
 * site's product shots render the same list, so this file reads no data and
 * names its group by the contract type, never the app's data client.
 */

/** What a group section needs from the navigation to arrange its group. */
export type ChannelGroupSectionActions = {
  collapsed: ReadonlySet<string>;
  onToggle: (group: ChannelGroupListing) => void;
  onRename: (group: ChannelGroupListing) => void;
  onMove: (group: ChannelGroupListing, offset: -1 | 1) => void;
  onDelete: (group: ChannelGroupListing) => void;
};

export function ChannelGroupSection(props: {
  actions: ChannelGroupSectionActions;
  group: ChannelGroupListing;
  headerClassName: string;
  index: number;
  count: number;
  children: ReactNode[];
}) {
  const { actions, group } = props;
  const collapsed = actions.collapsed.has(group.id);
  return (
    <section className="pb-0.5">
      <div className={cn(props.headerClassName, "group/header")}>
        <button
          type="button"
          aria-expanded={!collapsed}
          onClick={() => actions.onToggle(group)}
          className="flex min-w-0 items-center gap-1 hover:text-foreground"
        >
          <ChevronRightIcon
            aria-hidden
            className={cn("size-3 shrink-0 transition-transform", !collapsed && "rotate-90")}
          />
          <h2 className="truncate">{group.name}</h2>
        </button>
        <div className="ml-auto -mr-1 opacity-0 focus-within:opacity-100 group-hover/header:opacity-100">
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button aria-label={`${group.name} group options`} size="icon-xs" variant="ghost" />
              }
            >
              <EllipsisIcon className="size-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={4}>
              <DropdownMenuItem onClick={() => actions.onRename(group)}>
                <PencilIcon />
                Rename group
              </DropdownMenuItem>
              <DropdownMenuItem disabled={props.index === 0} onClick={() => actions.onMove(group, -1)}>
                <ArrowUpIcon />
                Move up
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={props.index === props.count - 1}
                onClick={() => actions.onMove(group, 1)}
              >
                <ArrowDownIcon />
                Move down
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => actions.onDelete(group)}>
                <Trash2Icon />
                Delete group
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {collapsed ? null : props.children.length > 0 ? props.children : (
        <p className="px-3 pb-1.5 text-2xs leading-5 text-tertiary-foreground">No channels</p>
      )}
    </section>
  );
}
